import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { PageHeader } from "@/components/AppShell";
import { Button, Field, Modal, TextInput } from "@/components/ui";
import { authError, CodeField } from "./AdminAuthFlow";

type AdminAccount = {
  id: string;
  email: string;
  display_name: string;
  is_active: boolean;
  mfa_enabled: boolean;
  created_at: string;
};
type Invitation = {
  id: string;
  email: string;
  display_name: string;
  created_at: string;
};
type Roster = { accounts: AdminAccount[]; invitations: Invitation[] };
type Action =
  | { kind: "invite" }
  | { kind: "account"; account: AdminAccount }
  | { kind: "revoke"; invitation: Invitation };

export function AdministratorsPage() {
  const { t } = useTranslation();
  const { account: current } = useAuth();
  const [roster, setRoster] = useState<Roster | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [action, setAction] = useState<Action | null>(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [invitationLink, setInvitationLink] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    try {
      setRoster(await api.accounts());
      setError(null);
    } catch (caught) {
      setError(authError(caught, t));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void refresh();
  }, []);

  function close() {
    setAction(null);
    setEmail("");
    setName("");
    setPassword("");
    setCode("");
    setActionError(null);
    setInvitationLink(null);
  }
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!action || busy) return;
    setBusy(true);
    setActionError(null);
    try {
      if (action.kind === "invite") {
        const result = await api.inviteAdmin(
          email.trim(),
          name.trim(),
          password,
          code,
        );
        setInvitationLink(result.activation_url);
      } else if (action.kind === "account") {
        await api.setAdminActive(
          action.account.id,
          !action.account.is_active,
          password,
          code,
        );
        close();
        await refresh();
      } else {
        await api.revokeInvitation(action.invitation.id, password, code);
        close();
        await refresh();
      }
      setPassword("");
      setCode("");
    } catch (caught) {
      setActionError(authError(caught, t));
    } finally {
      setBusy(false);
    }
  }
  const actionTitle =
    action?.kind === "invite"
      ? t("administrators.invite")
      : action?.kind === "revoke"
        ? t("administrators.revokeInvite")
        : action?.account.is_active
          ? t("administrators.deactivate")
          : t("administrators.reactivate");
  return (
    <div className="max-w-4xl">
      <PageHeader
        title={t("administrators.title")}
        subtitle={t("administrators.subtitle")}
        actions={
          <Button
            variant="primary"
            onClick={() => setAction({ kind: "invite" })}
          >
            {t("administrators.invite")}
          </Button>
        }
      />
      {error && (
        <p role="alert" className="mb-4 text-sm text-danger">
          {error}{" "}
          <Button
            onClick={() => {
              void refresh();
            }}
          >
            {t("common.retry")}
          </Button>
        </p>
      )}
      {loading && !roster ? (
        <p className="text-sm text-ink-subtle">{t("common.loading")}</p>
      ) : null}
      {roster && (
        <div className="space-y-6">
          <section className="card p-5">
            <h2 className="mb-3 font-semibold text-ink">
              {t("administrators.accounts")}
            </h2>
            <div className="divide-y divide-line">
              {roster.accounts.map((item) => (
                <div
                  key={item.id}
                  className="flex flex-wrap items-center justify-between gap-3 py-3"
                >
                  <div>
                    <p className="font-medium text-ink">{item.display_name}</p>
                    <p className="text-sm text-ink-subtle">{item.email}</p>
                    <p className="text-xs text-ink-subtle">
                      {t(
                        item.is_active
                          ? "administrators.active"
                          : "administrators.inactive",
                      )}
                    </p>
                  </div>
                  <Button
                    disabled={item.id === current?.id}
                    onClick={() =>
                      setAction({ kind: "account", account: item })
                    }
                  >
                    {t(
                      item.is_active
                        ? "administrators.deactivate"
                        : "administrators.reactivate",
                    )}
                  </Button>
                </div>
              ))}
              {roster.accounts.length === 0 && (
                <p className="py-3 text-sm text-ink-subtle">
                  {t("administrators.noAccounts")}
                </p>
              )}
            </div>
          </section>
          <section className="card p-5">
            <h2 className="mb-3 font-semibold text-ink">
              {t("administrators.pending")}
            </h2>
            <div className="divide-y divide-line">
              {roster.invitations.map((item) => (
                <div
                  key={item.id}
                  className="flex flex-wrap items-center justify-between gap-3 py-3"
                >
                  <div>
                    <p className="font-medium text-ink">{item.display_name}</p>
                    <p className="text-sm text-ink-subtle">{item.email}</p>
                  </div>
                  <Button
                    onClick={() =>
                      setAction({ kind: "revoke", invitation: item })
                    }
                  >
                    {t("administrators.revokeInvite")}
                  </Button>
                </div>
              ))}
              {roster.invitations.length === 0 && (
                <p className="py-3 text-sm text-ink-subtle">
                  {t("administrators.noPending")}
                </p>
              )}
            </div>
          </section>
        </div>
      )}
      {action && (
        <Modal
          open
          onClose={() => {
            if (!busy) close();
          }}
          title={actionTitle}
          footer={
            invitationLink ? (
              <Button
                variant="primary"
                onClick={() => {
                  close();
                  void refresh();
                }}
              >
                {t("administrators.done")}
              </Button>
            ) : undefined
          }
        >
          {invitationLink ? (
            <div className="space-y-3 text-sm text-ink">
              <p>{t("administrators.inviteCreated", { email })}</p>
              <p className="text-ink-subtle">
                {t("administrators.inviteValidity")}
              </p>
              <p className="text-ink-subtle">
                {t("administrators.inviteInstructions")}
              </p>
              <a
                className="block break-all text-brand underline"
                href={invitationLink}
              >
                {invitationLink}
              </a>
              <Button
                onClick={() => {
                  void navigator.clipboard
                    .writeText(invitationLink)
                    .catch(() => setActionError(t("adminAuth.copyFailed")));
                }}
              >
                {t("administrators.copyLink")}
              </Button>
              {actionError && (
                <p role="alert" className="text-danger">
                  {actionError}
                </p>
              )}
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              {action.kind === "invite" && (
                <>
                  <Field label={t("login.email")}>
                    {(id, describedBy) => (
                      <TextInput
                        id={id}
                        aria-describedby={describedBy}
                        type="email"
                        autoComplete="off"
                        required
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                      />
                    )}
                  </Field>
                  <Field label={t("administrators.name")}>
                    {(id, describedBy) => (
                      <TextInput
                        id={id}
                        aria-describedby={describedBy}
                        required
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                      />
                    )}
                  </Field>
                </>
              )}
              {action.kind !== "invite" && (
                <p className="text-sm text-ink">
                  {action.kind === "account"
                    ? action.account.email
                    : action.invitation.email}
                </p>
              )}
              <Field label={t("login.password")}>
                {(id, describedBy) => (
                  <TextInput
                    id={id}
                    aria-describedby={describedBy}
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                  />
                )}
              </Field>
              <CodeField code={code} setCode={setCode} />
              {actionError && (
                <p role="alert" className="text-sm text-danger">
                  {actionError}
                </p>
              )}
              <div className="flex justify-end gap-2">
                <Button onClick={close} disabled={busy}>
                  {t("common.cancel")}
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  loading={busy}
                  disabled={
                    !password ||
                    code.length !== 6 ||
                    (action.kind === "invite" && (!email || !name))
                  }
                >
                  {actionTitle}
                </Button>
              </div>
            </form>
          )}
        </Modal>
      )}
    </div>
  );
}
